/**
 * EnSLAPage.jsx — Service Level Agreement (English)
 * Public route: /en/sla
 *
 * Required for Azure Marketplace Partner Center submission (Gate 2).
 * EN version of SLAPage.jsx — do NOT edit PT version here.
 *
 * Last updated: May 29, 2026
 */

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  CheckCircle, Clock, AlertTriangle, Zap, Shield, Mail, ExternalLink,
  ChevronDown, ChevronUp,
} from 'lucide-react';
import SEOHead from '../../components/seo/SEOHead';

const BRAND_GREEN = '#064e3b';
const BRAND_GOLD  = '#b48c2c';

const SectionTitle = ({ children }) => (
  <h2 className="text-2xl font-bold mb-5" style={{ color: BRAND_GREEN }}>{children}</h2>
);

const Card = ({ children, highlight }) => (
  <div
    className="bg-white rounded-xl border-2 p-6 mb-4"
    style={{ borderColor: highlight ? BRAND_GREEN : '#e5e7eb' }}
  >
    {children}
  </div>
);

const Pill = ({ children, color = 'green' }) => {
  const map = {
    green: { bg: '#d1fae5', text: '#065f46' },
    gold:  { bg: '#fef3c7', text: '#92400e' },
    red:   { bg: '#fee2e2', text: '#991b1b' },
    gray:  { bg: '#f3f4f6', text: '#374151' },
    blue:  { bg: '#dbeafe', text: '#1e40af' },
  };
  const s = map[color] || map.green;
  return (
    <span className="inline-block px-3 py-1 rounded-full text-xs font-bold"
      style={{ backgroundColor: s.bg, color: s.text }}>
      {children}
    </span>
  );
};

const Row = ({ label, value, bold }) => (
  <div className="flex items-start justify-between py-2 border-b border-gray-100 last:border-0 gap-4">
    <span className="text-sm text-gray-600 flex-shrink-0">{label}</span>
    <span className={`text-sm text-right ${bold ? 'font-semibold text-gray-900' : 'text-gray-700'}`}>
      {value}
    </span>
  </div>
);

const Faq = ({ q, a }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-gray-200 rounded-xl mb-3 overflow-hidden">
      <button
        className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-gray-50 transition-colors"
        onClick={() => setOpen(o => !o)}
      >
        <span className="font-semibold text-sm text-gray-800">{q}</span>
        {open
          ? <ChevronUp size={16} className="text-gray-400 flex-shrink-0" />
          : <ChevronDown size={16} className="text-gray-400 flex-shrink-0" />}
      </button>
      {open && (
        <div className="px-5 pb-4 text-sm text-gray-600 leading-relaxed border-t border-gray-100 bg-gray-50">
          {a}
        </div>
      )}
    </div>
  );
};

export default function EnSLAPage() {
  return (
    <>
      <SEOHead
        title="Service Level Agreement (SLA)"
        description="VUKZ AI SLA — uptime commitments, support tiers, incident response times, backup and recovery for Azure Marketplace customers."
        path="/en/sla"
        lang="en"
        alternatePath="/sla"
      />
      <div style={{ backgroundColor: '#f8fafc', minHeight: '100vh' }}>

        {/* Hero */}
        <div
          style={{ background: `linear-gradient(135deg, ${BRAND_GREEN} 0%, #047857 100%)` }}
          className="py-16 px-4"
        >
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-white/10 rounded-full px-4 py-1.5 mb-5">
              <Shield size={14} className="text-green-300" />
              <span className="text-xs font-semibold text-green-200 uppercase tracking-wider">
                Official Documentation
              </span>
            </div>
            <h1 className="text-4xl font-extrabold text-white mb-4">
              Service Level Agreement
            </h1>
            <p className="text-green-100 text-lg mb-2">VUKZ AI — Sports Intelligence OS</p>
            <p className="text-green-200 text-sm">Last updated: May 29, 2026 · Version 1.0</p>
            <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
              <Link to="/en/terms" className="text-sm text-white/80 hover:text-white underline">
                Terms of Use
              </Link>
              <span className="text-white/40">·</span>
              <Link to="/en/privacy" className="text-sm text-white/80 hover:text-white underline">
                Privacy Policy
              </Link>
              <span className="text-white/40">·</span>
              <a href="mailto:support@vukz.ai" className="text-sm text-white/80 hover:text-white underline">
                support@vukz.ai
              </a>
              <span className="text-white/40">·</span>
              <Link to="/sla" className="text-sm text-white/60 hover:text-white/80 underline">
                🇧🇷 Versão PT
              </Link>
            </div>
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-4 py-10 space-y-12">

          {/* 1. Scope */}
          <section>
            <SectionTitle>1. Scope and Objective</SectionTitle>
            <Card>
              <p className="text-gray-700 leading-relaxed mb-4">
                This Service Level Agreement (<strong>"SLA"</strong>) establishes the commitments
                for availability, performance, support and maintenance of the{' '}
                <strong>VUKZ platform</strong>, operated by{' '}
                <strong>Arena Stats Ltda. (CNPJ 61.698.906/0001-16, Brazil)</strong>, trading as
                VUKZ AI, headquartered in Belo Horizonte, MG, Brazil.
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">
                This SLA applies to all customers with an active subscription (Sports Intelligence
                OS and Governance Intelligence OS plans), acquired directly or via{' '}
                <strong>Microsoft Azure Marketplace</strong>.
              </p>
              <p className="text-gray-700 leading-relaxed">
                The terms described herein supplement the service agreement or Terms of Use accepted
                on the platform and take precedence over informal communications.
              </p>
            </Card>
          </section>

          {/* 2. Availability */}
          <section>
            <SectionTitle>2. Service Availability</SectionTitle>

            <Card highlight>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center"
                  style={{ backgroundColor: '#d1fae5' }}>
                  <CheckCircle size={24} style={{ color: BRAND_GREEN }} />
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide font-medium">
                    Monthly Uptime Commitment
                  </p>
                  <p className="text-4xl font-extrabold" style={{ color: BRAND_GREEN }}>99.5%</p>
                </div>
              </div>
              <p className="text-sm text-gray-600">
                Uptime is measured monthly, excluding scheduled maintenance windows and events
                outside VUKZ AI's control (see Section 5). Calculation:{' '}
                <code className="bg-gray-100 px-1 rounded">
                  (available_minutes / total_month_minutes) × 100
                </code>.
              </p>
            </Card>

            <Card>
              <h3 className="font-bold text-gray-800 mb-4">Service Credits for SLA Breach</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="text-left px-4 py-2.5 font-semibold text-gray-600 rounded-tl-lg">Monthly uptime</th>
                      <th className="text-left px-4 py-2.5 font-semibold text-gray-600">Credit on monthly fee</th>
                      <th className="text-left px-4 py-2.5 font-semibold text-gray-600 rounded-tr-lg">Process</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-t border-gray-100">
                      <td className="px-4 py-3">≥ 99.5%</td>
                      <td className="px-4 py-3 text-gray-500">No credit</td>
                      <td className="px-4 py-3 text-gray-500">—</td>
                    </tr>
                    <tr className="border-t border-gray-100">
                      <td className="px-4 py-3">99.0% – 99.49%</td>
                      <td className="px-4 py-3"><Pill color="gold">10%</Pill></td>
                      <td className="px-4 py-3">Submit via support ticket</td>
                    </tr>
                    <tr className="border-t border-gray-100">
                      <td className="px-4 py-3">95.0% – 98.99%</td>
                      <td className="px-4 py-3"><Pill color="red">25%</Pill></td>
                      <td className="px-4 py-3">Submit via support ticket</td>
                    </tr>
                    <tr className="border-t border-gray-100">
                      <td className="px-4 py-3">&lt; 95.0%</td>
                      <td className="px-4 py-3"><Pill color="red">50%</Pill></td>
                      <td className="px-4 py-3">Applied automatically</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p className="text-xs text-gray-400 mt-3">
                Credits are applied as a discount on the next billing cycle and are not redeemable
                for cash. Maximum total credits in any given month: 50% of that month's subscription fee.
              </p>
              <p className="text-xs text-gray-400 mt-2">
                <strong>Azure Marketplace customers:</strong> SLA credits will be processed via
                Microsoft Partner Center refund policies.
              </p>
            </Card>
          </section>

          {/* 3. Support */}
          <section>
            <SectionTitle>3. Support Channels and Tiers</SectionTitle>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              {/* Starter */}
              <div className="bg-white rounded-xl border border-gray-200 p-5 flex flex-col">
                <div className="mb-3"><Pill color="gray">Starter Sports</Pill></div>
                <h3 className="font-bold text-gray-900 mb-3">Basic Support</h3>
                <ul className="text-sm text-gray-600 space-y-2 flex-1">
                  {[
                    'Email: support@vukz.ai',
                    'Hours: Mon–Fri, 9 AM–6 PM BRT',
                    'Initial response: up to 48 business hours',
                    'Self-service knowledge base',
                  ].map(i => (
                    <li key={i} className="flex items-start gap-2">
                      <CheckCircle size={14} className="mt-0.5 flex-shrink-0" style={{ color: BRAND_GREEN }} />
                      <span>{i}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Club */}
              <div className="bg-white rounded-xl border-2 p-5 flex flex-col" style={{ borderColor: BRAND_GREEN }}>
                <div className="mb-3"><Pill color="green">Club Performance</Pill></div>
                <h3 className="font-bold text-gray-900 mb-3">Standard Support</h3>
                <ul className="text-sm text-gray-600 space-y-2 flex-1">
                  {[
                    'Email + ticket portal',
                    'Hours: Mon–Fri, 8 AM–8 PM BRT',
                    'Response time: up to 24 business hours',
                    'Guided onboarding (1 session)',
                    'Incident status updates via email',
                  ].map(i => (
                    <li key={i} className="flex items-start gap-2">
                      <CheckCircle size={14} className="mt-0.5 flex-shrink-0" style={{ color: BRAND_GREEN }} />
                      <span>{i}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Enterprise */}
              <div className="rounded-xl p-5 flex flex-col"
                style={{ background: `linear-gradient(135deg, ${BRAND_GREEN}10 0%, ${BRAND_GOLD}10 100%)`, border: `2px solid ${BRAND_GOLD}` }}>
                <div className="mb-3"><Pill color="gold">Federation / Enterprise</Pill></div>
                <h3 className="font-bold text-gray-900 mb-3">Premium Support</h3>
                <ul className="text-sm text-gray-600 space-y-2 flex-1">
                  {[
                    'Email, ticket portal, dedicated channel',
                    'Hours: 24/7 for critical incidents',
                    'Response: 4h (critical) / 8h (high)',
                    'Dedicated Customer Success Manager',
                    'Full onboarding + training',
                    'Quarterly technical review',
                    'Support available in English',
                  ].map(i => (
                    <li key={i} className="flex items-start gap-2">
                      <Zap size={14} className="mt-0.5 flex-shrink-0" style={{ color: BRAND_GOLD }} />
                      <span>{i}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </section>

          {/* 4. Incident Classification */}
          <section>
            <SectionTitle>4. Incident Classification and Resolution Times</SectionTitle>
            <Card>
              <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr className="bg-gray-50">
                      {['Severity', 'Description', '1st Response', 'Target Resolution', 'Plans'].map(h => (
                        <th key={h} className="text-left px-4 py-2.5 font-semibold text-gray-600">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    <tr>
                      <td className="px-4 py-3"><Pill color="red">P1 — Critical</Pill></td>
                      <td className="px-4 py-3 text-gray-700">Platform completely inaccessible or confirmed data loss</td>
                      <td className="px-4 py-3 font-semibold" style={{ color: BRAND_GREEN }}>1h</td>
                      <td className="px-4 py-3">4h</td>
                      <td className="px-4 py-3"><Pill color="gold">Enterprise</Pill></td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3"><Pill color="red">P2 — High</Pill></td>
                      <td className="px-4 py-3 text-gray-700">Core functionality degraded; impact on multiple users</td>
                      <td className="px-4 py-3 font-semibold" style={{ color: BRAND_GREEN }}>4h</td>
                      <td className="px-4 py-3">24h</td>
                      <td className="px-4 py-3"><Pill color="gold">Club+</Pill></td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3"><Pill color="gold">P3 — Medium</Pill></td>
                      <td className="px-4 py-3 text-gray-700">Non-critical feature affected; workaround available</td>
                      <td className="px-4 py-3 font-semibold" style={{ color: BRAND_GREEN }}>24h</td>
                      <td className="px-4 py-3">72h</td>
                      <td className="px-4 py-3"><Pill color="gray">All</Pill></td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3"><Pill color="gray">P4 — Low</Pill></td>
                      <td className="px-4 py-3 text-gray-700">Cosmetic issue, usage question, feature request</td>
                      <td className="px-4 py-3 font-semibold" style={{ color: BRAND_GREEN }}>48h</td>
                      <td className="px-4 py-3">Next cycle</td>
                      <td className="px-4 py-3"><Pill color="gray">All</Pill></td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p className="text-xs text-gray-400 mt-3">
                Response times for Starter plans are counted in business hours (Mon–Fri, 9 AM–6 PM BRT).
                Enterprise plans count in calendar hours for P1 and P2.
              </p>
            </Card>
          </section>

          {/* 5. Maintenance */}
          <section>
            <SectionTitle>5. Scheduled Maintenance and Exclusions</SectionTitle>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Card>
                <div className="flex items-center gap-2 mb-3">
                  <Clock size={18} style={{ color: BRAND_GREEN }} />
                  <h3 className="font-bold text-gray-800">Maintenance Windows</h3>
                </div>
                <Row label="Standard window" value="Bi-weekly, Tuesdays 02:00–04:00 AM BRT" />
                <Row label="Advance notice" value="48 business hours via email" />
                <Row label="Max duration" value="2 hours per scheduled window" />
                <Row label="Emergency maintenance" value="No prior notice; immediate notification" />
                <p className="text-xs text-gray-400 mt-3">
                  Scheduled windows are not counted towards uptime calculation.
                </p>
              </Card>
              <Card>
                <div className="flex items-center gap-2 mb-3">
                  <AlertTriangle size={18} style={{ color: BRAND_GOLD }} />
                  <h3 className="font-bold text-gray-800">SLA Exclusions</h3>
                </div>
                <ul className="text-sm text-gray-600 space-y-2">
                  {[
                    'Third-party infrastructure failures (Azure, DNS providers, CDN)',
                    'Downtime caused by misuse, abusive scripts or customer-originated DDoS',
                    'Force majeure: natural disasters, pandemics, regulatory interruptions',
                    'Periods of suspended account due to non-payment',
                    'Features marked as "Beta" or "Preview"',
                  ].map(item => (
                    <li key={item} className="flex items-start gap-2">
                      <span className="mt-1 w-1.5 h-1.5 rounded-full bg-gray-400 flex-shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </Card>
            </div>
          </section>

          {/* 6. Backup */}
          <section>
            <SectionTitle>6. Backup and Data Recovery</SectionTitle>
            <Card>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <h3 className="font-semibold text-gray-800 mb-3">Backup Policy</h3>
                  <Row label="Frequency" value="Daily (automated)" />
                  <Row label="Retention" value="30 days" />
                  <Row label="Storage" value="Azure SQL — Geo-redundant (Brazil South + US)" />
                  <Row label="Encryption" value="AES-256 at rest and in transit (TLS 1.2+)" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-800 mb-3">Disaster Recovery</h3>
                  <Row label="RPO (Recovery Point Objective)" value="Up to 24 hours" />
                  <Row label="RTO (Recovery Time Objective)" value="Up to 4h (Enterprise) / 8h (others)" />
                  <Row label="DR Plan" value="Azure Region Failover (automated)" />
                  <Row label="DR Tests" value="Annual" />
                </div>
              </div>
            </Card>
          </section>

          {/* 7. Security */}
          <section>
            <SectionTitle>7. Security and Compliance</SectionTitle>
            <Card>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {[
                  { label: 'Infrastructure', value: 'Microsoft Azure (Brazil South)', icon: '☁️' },
                  { label: 'Compliance', value: 'LGPD (Law 13.709/2018)', icon: '🛡️' },
                  { label: 'Authentication', value: 'JWT + RBAC per role', icon: '🔐' },
                  { label: 'Sensitive data', value: 'Encrypted (AES-256)', icon: '🔒' },
                  { label: 'Monitoring', value: '24/7 — Microsoft Purview + Azure Monitor', icon: '📊' },
                  { label: 'Penetration testing', value: 'Annual, by independent third parties', icon: '🔍' },
                  { label: 'Vulnerability management', value: 'Critical patches within 72 hours', icon: '⚡' },
                  { label: 'DPO', value: 'Paulo Brandt — privacy@vukz.ai', icon: '👤' },
                ].map(item => (
                  <div key={item.label} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                    <span className="text-xl">{item.icon}</span>
                    <div>
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{item.label}</p>
                      <p className="text-sm text-gray-800 mt-0.5">{item.value}</p>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          </section>

          {/* 8. Credit Request */}
          <section>
            <SectionTitle>8. How to Request an SLA Credit</SectionTitle>
            <Card>
              <ol className="space-y-4">
                {[
                  { n: '1', title: 'Identify the downtime period', desc: 'Record the start and end dates/times of the incident and the impact observed on the platform.' },
                  { n: '2', title: 'Open a credit request ticket', desc: 'Send an email to support@vukz.ai with the subject "SLA Credit Request — [month/year]", including the incident ID (if available) and evidence (screenshots, logs).' },
                  { n: '3', title: 'Request deadline', desc: 'Requests must be submitted within 30 days after the end of the month in which the incident occurred.' },
                  { n: '4', title: 'Analysis and approval', desc: 'VUKZ AI will analyze the request within 10 business days, consulting availability logs (Azure Monitor). The customer will be notified of approval or rejection with justification.' },
                  { n: '5', title: 'Credit application', desc: 'Approved credits will be applied to the next invoice as a discount. No cash payment. Azure Marketplace customers: credits processed via Microsoft Partner Center.' },
                ].map(step => (
                  <li key={step.n} className="flex gap-4">
                    <div className="w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0 mt-0.5"
                      style={{ backgroundColor: BRAND_GREEN }}>
                      {step.n}
                    </div>
                    <div>
                      <p className="font-semibold text-gray-800 text-sm">{step.title}</p>
                      <p className="text-sm text-gray-600 mt-0.5">{step.desc}</p>
                    </div>
                  </li>
                ))}
              </ol>
            </Card>
          </section>

          {/* 9. Status */}
          <section>
            <SectionTitle>9. Incident Status and Communications</SectionTitle>
            <Card>
              <Row label="Status page" value="status.vukz.ai (coming soon)" bold />
              <Row label="Incident notifications" value="Email to registered technical contacts" />
              <Row label="Progress updates (P1/P2)" value="Every 1 hour during active incident" />
              <Row label="Post-mortem (P1)" value="Report within 5 business days after resolution" />
              <Row label="Maintenance notices" value="Email 48 hours in advance" />
            </Card>
          </section>

          {/* FAQ */}
          <section>
            <SectionTitle>Frequently Asked Questions</SectionTitle>
            <Faq
              q="Does the SLA apply to Beta or Preview features?"
              a="No. Features marked as 'Beta', 'Preview' or 'Experimental' in the interface are excluded from uptime commitments and SLA credits."
            />
            <Faq
              q="What happens if I purchased via Azure Marketplace?"
              a="The SLA applies equally regardless of the acquisition channel. Azure Marketplace customers should open tickets at support@vukz.ai. SLA credits will be processed via Microsoft Partner Center refund policies."
            />
            <Faq
              q="Can I cancel the service if the SLA is systematically breached?"
              a="Yes. If uptime falls below 95% for two consecutive months, the customer has the right to terminate the contract without penalty, upon written notice within 30 days after the second affected month."
            />
            <Faq
              q="How is uptime measured?"
              a="Uptime is measured by our monitoring infrastructure (Azure Monitor + Application Insights), recording the availability of the main API endpoint and the web interface. Logs are auditable and can be shared in credit disputes."
            />
            <Faq
              q="Is support available in English?"
              a="Yes. Support is provided primarily in Portuguese (Brazil) but we also serve customers in English for international clients (Enterprise and Azure Marketplace). For customers from regions other than Brazil, English support is available via email."
            />
          </section>

          {/* Contact */}
          <section>
            <SectionTitle>Contact and Support Channels</SectionTitle>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {[
                { href: 'mailto:support@vukz.ai', icon: Mail, title: 'General Support', sub: 'support@vukz.ai' },
                { href: 'mailto:privacy@vukz.ai', icon: Shield, title: 'Privacy / DPO', sub: 'privacy@vukz.ai' },
                { href: 'https://www.vukz.ai', icon: ExternalLink, title: 'Website', sub: 'www.vukz.ai', external: true },
              ].map(({ href, icon: Icon, title, sub, external }) => (
                <a key={title}
                  href={href}
                  {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                  className="flex flex-col items-center text-center p-6 bg-white rounded-xl border border-gray-200 hover:border-green-300 hover:shadow-md transition-all group">
                  <Icon size={28} className="mb-2 group-hover:scale-110 transition-transform" style={{ color: BRAND_GREEN }} />
                  <p className="font-bold text-gray-800 text-sm">{title}</p>
                  <p className="text-xs text-gray-500 mt-1">{sub}</p>
                </a>
              ))}
            </div>
          </section>

          {/* Footer */}
          <div className="pt-8 border-t border-gray-200 text-center text-sm text-gray-500">
            <p>VUKZ AI · Arena Stats Ltda. · CNPJ 61.698.906/0001-16 · Belo Horizonte, Brazil</p>
            <p className="mt-2">
              <Link to="/en/terms" className="hover:underline mx-2" style={{ color: BRAND_GREEN }}>Terms of Use</Link>
              ·
              <Link to="/en/privacy" className="hover:underline mx-2" style={{ color: BRAND_GREEN }}>Privacy Policy</Link>
              ·
              <span className="mx-2 font-semibold" style={{ color: BRAND_GREEN }}>SLA (this document)</span>
            </p>
            <p className="mt-2 text-xs text-gray-400">Version 1.0 — May 29, 2026</p>
          </div>

        </div>
      </div>
    </>
  );
}
